import type { VariableExpenseGridQuery } from '@clockwise/clients/customer-variable-expense-occurrences';
import {
  addIsoDays,
  type DateRangeValue,
  type ResolvedDateRange,
  resolveDateRange,
} from '@clockwise/shared';
import { prettyDateRange } from '@clockwise/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { variableExpenseOccurrencesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** One page of the grid. Lines, not occurrences — see the client. */
export const GRID_PAGE_SIZE = 100;

/** The half-open service period an invoice stores. */
export interface GridPeriod {
  periodStart: string;
  periodEnd: string;
}

/**
 * A month (`'2026-09-01'`) as the half-open service period an invoice stores.
 *
 * The end is the FIRST day of the next month, exclusive, which is how
 * `service_period_end` is stored and what `priceCustomerExpenses` compares
 * against. Deriving it here — once — is what keeps the grid, the export, the
 * template and the invoice from disagreeing about which side of a boundary a
 * fee on the 30th falls.
 *
 * UTC-pinned: these are calendar days with no instant behind them, and
 * building them from a local-zone `Date` would shift the boundary by a day for
 * anyone west of UTC.
 */
export function monthToPeriod(month: string): GridPeriod {
  const [y, m] = month.split('-');
  const year = Number(y);
  const monthNo = Number(m);
  const next = new Date(Date.UTC(year, monthNo, 1));
  const end = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-01`;
  return { periodStart: month, periodEnd: end };
}

/**
 * A resolved date range as the same half-open period.
 *
 * `ResolvedDateRange` is INCLUSIVE on both ends — "Sep 1 – Sep 30" — and the
 * API's window is `[start, end)`. The +1 day is the whole conversion, and it
 * lives here rather than at the call sites so a range can never be sent one
 * day short. Getting it wrong drops every fee dated on the last day of the
 * window, which is the failure that looks like nothing at all.
 */
export function rangeToPeriod(range: ResolvedDateRange): GridPeriod {
  return { periodStart: range.from, periodEnd: addIsoDays(range.to, 1) };
}

/** `'2026-09-01'` → `'September 2026'`. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-');
  const name = new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${name} ${y}`;
}

/**
 * How a window READS in a heading.
 *
 * A window that is exactly one calendar month says so — "September 2026" —
 * because that is what it is and what every other invoicing screen calls it.
 * Anything else prints its dates. Without the special case the common range
 * would read "Sep 1 – Sep 30, 2026", which is the same fact in more words and
 * stops matching the month the invoice will carry.
 */
export function describePeriod(period: GridPeriod): string {
  const { periodStart, periodEnd } = period;
  if (periodStart.endsWith('-01') && monthToPeriod(periodStart).periodEnd === periodEnd) {
    return monthLabel(periodStart);
  }
  return prettyDateRange({ from: periodStart, to: addIsoDays(periodEnd, -1) });
}

/**
 * The window a user most likely means: the month just ended.
 *
 * A DEFAULT only — the filter bar owns the choice from there, and the server
 * derives nothing from this. Variable expenses are recorded after the fact, so
 * the current month is almost never the one being filled in.
 *
 * Stored as the PRESET, not as the dates it resolves to, so a bookmarked link
 * still means "the month before today" when it is opened next month.
 */
export const DEFAULT_RANGE: DateRangeValue = { preset: 'previous_month' };

/**
 * Read the window out of the route's search params.
 *
 * `month=2026-09-01` is still honoured — the generate dialog links here with
 * the period it is about to invoice, and those links are in the wild — and is
 * expanded to the equivalent custom range. Explicit range params win over it.
 */
export function searchToRange(search: {
  preset?: string;
  from?: string;
  to?: string;
  month?: string;
}): DateRangeValue {
  if (search.preset === 'custom') {
    return { preset: 'custom', from: search.from ?? '', to: search.to ?? '' };
  }
  if (search.preset) return { preset: search.preset as DateRangeValue['preset'] };
  if (search.month) {
    const { periodEnd } = monthToPeriod(search.month);
    return { preset: 'custom', from: search.month, to: addIsoDays(periodEnd, -1) };
  }
  return DEFAULT_RANGE;
}

/** The resolved period for a selection, or `null` while a Custom range is
 *  half-picked (one bound, or inverted) — which is what disables the query
 *  rather than firing it against a window the user has not finished naming. */
export function rangeToPeriodOrNull(value: DateRangeValue): GridPeriod | null {
  const resolved = resolveDateRange(value);
  return resolved ? rangeToPeriod(resolved) : null;
}

/**
 * The grid for a period.
 *
 * `keepPreviousData` so paging and changing the window don't blank the table —
 * a screen someone is typing into must not lose its shape underneath them.
 */
export function useVariableExpenseGrid(
  period: GridPeriod | null,
  opts: {
    customerId?: string;
    expenseCatalogId?: string;
    offset?: number;
    state?: 'all' | 'needs';
  } = {},
) {
  const query: VariableExpenseGridQuery | null = period
    ? {
        ...period,
        ...(opts.customerId ? { customerId: opts.customerId } : {}),
        ...(opts.expenseCatalogId ? { expenseCatalogId: opts.expenseCatalogId } : {}),
        ...(opts.state ? { state: opts.state } : {}),
        limit: GRID_PAGE_SIZE,
        offset: opts.offset ?? 0,
      }
    : null;

  return useQuery({
    queryKey: queryKeys.variableExpenseOccurrences.grid({
      periodStart: period?.periodStart ?? '',
      periodEnd: period?.periodEnd ?? '',
      ...(opts.customerId ? { customerId: opts.customerId } : {}),
      ...(opts.expenseCatalogId ? { expenseCatalogId: opts.expenseCatalogId } : {}),
      ...(opts.state ? { state: opts.state } : {}),
      offset: opts.offset ?? 0,
    }),
    queryFn: () => variableExpenseOccurrencesApi.grid(query as VariableExpenseGridQuery),
    enabled: query !== null,
    placeholderData: keepPreviousData,
  });
}
